import type { Dayjs } from 'dayjs'
import type { FC } from 'react'

import { Button } from 'common/components/Button/Button'
import {
  BUTTON_CONSTANTS,
  BUTTON_MODIFIER,
  BUTTON_SEVERITY,
} from 'common/constants/buttonConstants'

import { NEW_PATIENT_FORM_KEYS } from 'features/Home/constants/infoNewPatientFormKeys'

import styles from './ehrAppointmentV2.module.scss'
import { formatDuration, formatMinutes } from './pairing'

export const footerTitleOf = (start: number, duration: number, providerName: string): string =>
  `${formatMinutes(start)} – ${formatMinutes(start + duration)} · ${formatDuration(
    duration,
  )} · ${providerName}`

// Errors already shown next to their field; the footer only reports the rest.
const INLINE_ERROR_KEYS: ReadonlySet<string> = new Set([
  NEW_PATIENT_FORM_KEYS.FIRST_NAME,
  NEW_PATIENT_FORM_KEYS.LAST_NAME,
  NEW_PATIENT_FORM_KEYS.ROOM,
  NEW_PATIENT_FORM_KEYS.PUNCTUALITY_MINUTES,
])

// Without this a schema failure on a field the dialog does not render makes Save look dead.
export const footerErrorOf = (errors: Record<string, unknown>): string | undefined =>
  Object.entries(errors)
    .filter(([key, message]) => message && !INLINE_ERROR_KEYS.has(key))
    .map(([, message]) => String(message))[0]

type SubtitleArgs = { day: Dayjs; roomName?: string; isLoadedDay: boolean }

export const footerSubtitleOf = ({ day, roomName, isLoadedDay }: SubtitleArgs): string =>
  [
    day.format('dddd, MMM D'),
    roomName ?? 'No room',
    isLoadedDay ? '' : 'conflicts not checked for this day',
  ]
    .filter(Boolean)
    .join(' · ')

type Props = {
  title: string
  subtitle: string
  error?: string
  isSaving: boolean
  canSave: boolean
  canDelete: boolean
  hasSearched: boolean
  onClearSearch: () => void
  onSave: () => void
  onDelete: () => void
  onCancel: () => void
}

export const FooterBar: FC<Props> = ({
  title,
  subtitle,
  error,
  isSaving,
  canSave,
  canDelete,
  hasSearched,
  onClearSearch,
  onSave,
  onDelete,
  onCancel,
}) => (
  <div className={styles.footer}>
    <div className={styles.footerSummary}>
      <span className={styles.footerKicker}>Appointment</span>
      <span className={styles.footerTitle}>{title}</span>
      <span className={error ? styles.errorText : styles.footerSub}>{error ?? subtitle}</span>
    </div>

    <div className={styles.fieldGrow} />

    <div className={styles.footerActions}>
      {hasSearched && <Button onClick={onClearSearch}>Clear search</Button>}

      <Button
        onClick={onSave}
        loading={isSaving}
        disabled={!canSave}
        data-cy='submit-btn'
        severity={BUTTON_SEVERITY.SUCCESS_FILLED}>
        {BUTTON_CONSTANTS.SAVE}
      </Button>

      {canDelete && (
        <Button
          onClick={onDelete}
          disabled={isSaving}
          severity={BUTTON_SEVERITY.DANGER}
          modifier={BUTTON_MODIFIER.PRIMARY}>
          {BUTTON_CONSTANTS.DELETE}
        </Button>
      )}

      <Button onClick={onCancel} data-cy='cancel-btn' disabled={isSaving}>
        {BUTTON_CONSTANTS.CANCEL}
      </Button>
    </div>
  </div>
)
